import { IncidentDetailsMessage } from "@/modules/PhishTitan/types/IncidentSummary";

const LAYER_7_ATTACK_PREVENTION_ELEMENTS_LIMIT = 1000;

const removeInlineStyles = (parsedDocument: Document) => {
  const elementsWithInlineStyles = parsedDocument.querySelectorAll("[style]");
  for (let i = 0; i <= LAYER_7_ATTACK_PREVENTION_ELEMENTS_LIMIT; i++) {
    if (!elementsWithInlineStyles[i]) {
      return;
    }
    elementsWithInlineStyles[i].removeAttribute("style");
  }
};
const removeInlineColor = (parsedDocument: Document) => {
  const elementsWithInlineColor = parsedDocument.querySelectorAll("[color]");
  for (let i = 0; i <= LAYER_7_ATTACK_PREVENTION_ELEMENTS_LIMIT; i++) {
    if (!elementsWithInlineColor[i]) {
      return;
    }
    elementsWithInlineColor[i].removeAttribute("color");
  }
};
const removeImageTags = (parsedDocument: Document) => {
  const imageTags = parsedDocument.querySelectorAll("img");
  for (let i = 0; i <= LAYER_7_ATTACK_PREVENTION_ELEMENTS_LIMIT; i++) {
    if (!imageTags[i]) {
      return;
    }
    imageTags[i].remove();
  }
};

const removeStyleTags = (parsedDocument: Document) => {
  const styleTags = parsedDocument.querySelectorAll("style");
  for (let i = 0; i <= LAYER_7_ATTACK_PREVENTION_ELEMENTS_LIMIT; i++) {
    if (!styleTags[i]) {
      return;
    }
    styleTags[i].remove();
  }
};

const removeMetaTags = (parsedDocument: Document) => {
  const metaTags = parsedDocument.querySelectorAll("meta");
  for (let i = 0; i <= LAYER_7_ATTACK_PREVENTION_ELEMENTS_LIMIT; i++) {
    if (!metaTags[i]) {
      return;
    }
    metaTags[i].remove();
  }
};

export type MessageForParse = Required<Pick<IncidentDetailsMessage, "body">> &
  Pick<IncidentDetailsMessage, "subject" | "sender_mail">;

export const parseInnerHtml = (
  message: MessageForParse,
  isThemeDark: boolean,
) => {
  const parser = new DOMParser();
  const innerHtml = message.body.content;

  const parsedDocument = parser.parseFromString(innerHtml, "text/html");

  removeInlineStyles(parsedDocument);
  removeInlineColor(parsedDocument);
  removeImageTags(parsedDocument);

  // Remove clare banner
  const clareBanner = parsedDocument.querySelector(".pt-banner-clare");
  if (clareBanner) {
    clareBanner.remove();
  }

  // Remove the existing head
  const existingHead = parsedDocument.querySelector("head");
  if (existingHead) {
    parsedDocument.documentElement.removeChild(existingHead);
  }

  removeStyleTags(parsedDocument);
  removeMetaTags(parsedDocument);

  // Add details about sender and subject
  const body = parsedDocument.querySelector("body");
  if (body) {
    const details = parsedDocument.createElement("div");

    const sender = parsedDocument.createElement("div");
    const senderPrefix = parsedDocument.createElement("span");
    senderPrefix.classList.add("text-bold");
    senderPrefix.textContent = "Sender: ";
    const senderName = parsedDocument.createElement("span");
    senderName.textContent = message.sender_mail ?? "";
    sender.appendChild(senderPrefix);
    sender.appendChild(senderName);

    const subject = parsedDocument.createElement("div");
    const subjectPrefix = parsedDocument.createElement("span");
    subjectPrefix.classList.add("text-bold");
    subjectPrefix.textContent = "Subject: ";
    const subjectName = parsedDocument.createElement("span");
    subjectName.textContent = message.subject ?? "";
    subject.appendChild(subjectPrefix);
    subject.appendChild(subjectName);

    const divider = parsedDocument.createElement("hr");
    const space = parsedDocument.createElement("br");

    details.appendChild(subject);
    details.appendChild(sender);
    details.appendChild(divider);
    details.appendChild(space);

    body.insertBefore(details, body.firstChild);
  }

  // Add a new styles
  const newStyle = parsedDocument.createElement("style");
  newStyle.innerHTML = `
  :root {
    --text: ${isThemeDark ? "#fff" : "#000"};
    --background: ${isThemeDark ? "#000" : "#fff"};
    --generic-greeting: ${isThemeDark ? "#07087A" : "#50A7D3"};
    --brand-impersonation: ${isThemeDark ? "#0B0ECB" : "#A5D9F2"};
    --spelling-grammar-errors: ${isThemeDark ? "#003D11" : "#49B675"};
    --request-financial-assistance: ${isThemeDark ? "#00661D" : "#9EDFA6"};
    --urgent-threatening-language: ${isThemeDark ? "#AB1212" : "#FF870F"};
    --request-for-pii: ${isThemeDark ? "#670B0B" : "#FFC77F"};
    --unexpected-reward: ${isThemeDark ? "#550174" : "#D6BDEA"};
    --unrealistic-offer: ${isThemeDark ? "#8E01C1" : "#E9DFF1"};
  }

    body,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    a,
    strong,
    p,
    li,
    b {
      font-size: 14px;
      text-align: left;
      font-weight: normal;
      font-family: Inter, sans-serif;
      line-height: 1.7;
    }
    a:link {
      text-decoration: none;
      color: var(--text);
    }
    body {
      background: ${isThemeDark ? "#000" : "#fff"};
      color: var(--text);
    }
    table {
      width: 100%;
    }
    .keyword {
      color: var(--text);
      border-radius: 2px;
    }
    .keyword--generic-greeting {
      background-color: var(--generic-greeting);
      border: solid 2px var(--generic-greeting);
    }
    .keyword--brand-impersonation {
      background-color: var(--brand-impersonation);
      border: solid 2px var(--brand-impersonation);
    }
    .keyword--spelling-grammar-errors {
      background-color: var(--spelling-grammar-errors);
      border: solid 2px var(--spelling-grammar-errors);
    }
    .keyword--request-financial-assistance {
      background-color: var(--request-financial-assistance);
      border: solid 2px var(--request-financial-assistance);
    }
    .keyword--urgent-threatening-language {
      background-color: var(--urgent-threatening-language);
      border: solid 2px var(--urgent-threatening-language);
    }
    .keyword--request-for-pii {
      background-color: var(--request-for-pii);
      border: solid 2px var(--request-for-pii);
    }
    .keyword--unexpected-reward {
      background-color: var(--unexpected-reward);
      border: solid 2px var(--unexpected-reward);
    }
    .keyword--unrealistic-offer {
      background-color: var(--unrealistic-offer);
      border: solid 2px var(--unrealistic-offer);
    }
    .keyword__tag {
      background-color: var(--background);
      padding: 0 4px;
    }
    .text-bold {
      font-weight: bold;
    }
`;
  parsedDocument.documentElement.appendChild(newStyle);

  return parsedDocument.documentElement.innerHTML;
};
